import React, { useCallback, useEffect, useRef, useState } from 'react'

import { Text } from '@cash-me/react-components'

import { pushDataLayer } from 'utils/push-data-layer'

import * as S from './styles'
import { TabProps, TabsProps } from './tabs.model'

export const Tabs = ({
  activeTabIndex,
  children,
  dataLayer,
  slidingIndicator = false,
  autoCenterActive = false
}: TabsProps) => {
  const [selectedTab, setSelectedTab] = useState<number>(activeTabIndex)
  const tabListRef = useRef<HTMLDivElement>(null)
  const [indicator, setIndicator] = useState<{ left: number; width: number }>({ left: 0, width: 0 })
  const tabsCount = React.Children.count(children)

  useEffect(() => {
    if (!slidingIndicator) return undefined

    const updateIndicator = () => {
      const activeButton = tabListRef.current?.querySelectorAll('button')[selectedTab]
      if (activeButton) setIndicator({ left: activeButton.offsetLeft, width: activeButton.offsetWidth })
    }

    updateIndicator()
    window.addEventListener('resize', updateIndicator)

    return () => window.removeEventListener('resize', updateIndicator)
  }, [slidingIndicator, selectedTab, tabsCount])

  // Slide the strip so the selected tab is centered (used on mobile, where the tabs overflow).
  useEffect(() => {
    if (!autoCenterActive) return

    const list = tabListRef.current
    const activeButton = list?.querySelectorAll('button')[selectedTab]
    if (list && activeButton) {
      const target = activeButton.offsetLeft - (list.clientWidth - activeButton.offsetWidth) / 2
      list.scrollTo({ left: Math.max(0, target), behavior: 'smooth' })
    }
  }, [autoCenterActive, selectedTab])

  const handleSelectTab = useCallback(
    (index: number) => {
      setSelectedTab(index)

      if (!dataLayer) return
      const data = dataLayer.find(item => item.index === index)
      if (data) pushDataLayer(data)
    },
    [dataLayer]
  )

  const renderTabs = useCallback(
    () =>
      React.Children.map(children, (child, index) => {
        if (!React.isValidElement<TabProps>(child)) return

        return (
          <S.Tab data-state={selectedTab === index ? 'active' : 'inactive'} onClick={() => handleSelectTab(index)}>
            <Text as="span" size="caption" uppercase color="var(--colors-neutral-text-weaker)">
              {child.props?.name}
            </Text>
          </S.Tab>
        )
      }),
    [children, selectedTab, handleSelectTab]
  )

  const renderTabContent = useCallback(
    () =>
      React.Children.map(children, (child, index) => (
        <S.TabContent data-state={selectedTab === index ? 'active' : 'inactive'} $active={selectedTab === index}>
          {child}
        </S.TabContent>
      )),
    [children, selectedTab]
  )

  return (
    <>
      <S.TabList ref={tabListRef} $sliding={slidingIndicator}>
        {renderTabs()}
        {slidingIndicator && (
          <S.Indicator
            className="tab-indicator"
            aria-hidden
            style={{ transform: `translateX(${indicator.left}px)`, width: indicator.width }}
          />
        )}
      </S.TabList>
      <S.TabContentWrapper>
        <S.Movable style={{ transform: `translateX(-${selectedTab * 100}%)` }}>{renderTabContent()}</S.Movable>
      </S.TabContentWrapper>
    </>
  )
}
